import {Observable} from 'rxjs';
import {HttpClient, HttpEvent, HttpEventType} from '@angular/common/http';

export class NetworkTester {

    // in bits per second
    averageDownloadBandwidth = null;
    downloadProgress = 0;

    // in bits per second
    averageUploadBandwidth = null;
    uploadProgress = 0;

    // in ms
    latencySum = 0;
    packetsReceived = 0;
    averageLatency = 0;

    // null if not yet tested, true if reachable, else false
    reachable = null;

    running = false;
    errorMessage = '';

    lastTimestamp = null;
    firstDownloadTimestamp = null;
    firstUploadTimestamp = null;

    constructor(private http: HttpClient, public downloadUrl: string = null, public uploadUrl: string = null, public connectionUrl: string = null) {

    }

    public progress() {
        if (this.downloadUrl !== null && this.uploadUrl !== null) {
            return Math.floor((this.downloadProgress + this.uploadProgress) * 50);
        } else if (this.downloadUrl !== null) {
            return Math.floor(this.downloadProgress * 100);
        } else if (this.uploadUrl !== null) {
            return Math.floor(this.uploadProgress * 100);
        } else {
            return 1;
        }
    }

    public runConnectionTest(callback = null) {
        // Only run a single test
        if (this.running) {
            return;
        }

        // Mark initial state
        this.running = true;
        this.resetState();
        // Define callback that will be called when test is finished
        const finalCallback = () => {
            // Make sure we notify that the test has finished running
            this.running = false;
            // Calling callback after current test is finished
            if (callback !== null) {
                callback();
            }
        };

        fetch(this.connectionUrl, { 
            mode: 'no-cors',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }

        })
            .then(() => {
                this.reachable = true
            })
            .catch(() => {
                this.reachable = false
                this.errorMessage = 'Server did not respond';
            });

        finalCallback();
    }

    public runTest(callback = null, downloadOptions = {}, uploadOptions = {}) {
        // Only run a single test
        if (this.running) {
            return;
        }

        // Mark initial state
        this.running = true;
        this.resetState();
        // Define callback that will be called when test is finished
        const finalCallback = () => {
            // Make sure we notify that the test has finished running
            this.running = false;
            // Calling callback after current test is finished
            if (callback !== null) {
                callback();
            }
        };

        // Check if we do a download test and an upload test
        if (this.downloadUrl !== null && this.uploadUrl !== null) {
            this.downloadAndLatencyTest(downloadOptions, () => {
                this.uploadAndLatencyTest(uploadOptions, finalCallback);
            });

        }

        // Check if we only do a download test
        else if (this.downloadUrl !== null) {
            this.downloadAndLatencyTest(downloadOptions, finalCallback);
        }

        // Check if we only do an upload test
        else if (this.uploadUrl !== null) {
            this.uploadAndLatencyTest(uploadOptions, finalCallback);
        }

        // If nothing happens we still call finalCallback
        else {
            finalCallback();
        }
    }

    private downloadAndLatencyTest(options: {}, callback) {
        // Creating an observable http request
        const getRequest = this.getWithProgress(this.downloadUrl, options);
        // Counting each http event for better precision
        // We wish to ignore the initial events due to DNS and handshaking
        let count = 0;
        // Test has started - expect server to be unreachable unless proven otherwise
        this.reachable = false;
        // Initial start estimate
        this.firstDownloadTimestamp = new Date().getTime();

        getRequest.subscribe(event => {
            const now = new Date().getTime();

            if (count === 1) {
                // If we get more than 1 event the server has responded!
                this.reachable = true;
                // Making sure we ignore the initial DNS time when calculating speed
                this.firstDownloadTimestamp = now;
            }
            // We ignore other events because they include server processing time
            if (event.type === HttpEventType.DownloadProgress) {
                // Calculating download bandwidth
                const fullTime = now - this.firstDownloadTimestamp;
                this.averageDownloadBandwidth = (event.loaded * 8 / 1000000) / (fullTime / 1000);
                this.downloadProgress = (event.loaded / event.total);

                // Calculate latency
                this.latencySum += now - this.lastTimestamp;
                this.packetsReceived++;
                this.averageLatency = this.latencySum / this.packetsReceived;
            }

            this.lastTimestamp = now;
            count++;
        }, error => {
            this.handleError(error);
            callback();
        }, () => {
            this.downloadProgress = 1;
            callback();
        });
    }

    private uploadAndLatencyTest(options: {}, callback) {
        // Creating an observable http request
        const postRequest = this.postWithProgress(this.uploadUrl, options);
        // Counting each http event for better precision
        // We wish to ignore the initial events due to DNS and handshaking
        let count = 0;
        // Test has started - expect server to be unreachable unless proven otherwise
        this.reachable = false;
        // Initial start estimate
        this.firstUploadTimestamp = new Date().getTime();

        postRequest.subscribe(event => {
            const now = new Date().getTime();

            if (count === 1) {
                // If we get more than 1 event server has responded!
                this.reachable = true;
                // Making sure we ignore the initial DNS time when calculating speed
                this.firstUploadTimestamp = now;
            }
            // We ignore other events because they include server processing time
            if (event.type === HttpEventType.UploadProgress) {
                // Calculating upload bandwidth
                const fullTime = now - this.firstUploadTimestamp;
                this.averageUploadBandwidth = (event.loaded * 8 / 1000000) / (fullTime / 1000);
                this.uploadProgress = (event.loaded / event.total);

                // Calculate latency
                this.latencySum += now - this.lastTimestamp;
                this.packetsReceived++;
                this.averageLatency = this.latencySum / this.packetsReceived;
            }

            this.lastTimestamp = now;
            count++;
        }, error => {
            this.handleError(error);
            this.uploadProgress = 1;
            callback();
        }, () => {
            this.uploadProgress = 1;
            callback();
        });
    }

    private handleError(error) {
        this.errorMessage = error.statusText;
        this.uploadProgress = 1;

        // If status is 0 we have not received any information back from the server
        if (error.status === 0 && !this.reachable) {
            this.errorMessage = 'Server did not respond';
        }
    }

    private getWithProgress(url: string, options = {}): Observable<HttpEvent<any>> {
        return this.http.get(url, {
            ...options, reportProgress: true, observe: 'events',
        });
    }

    private postWithProgress(url: string, options = {}): Observable<HttpEvent<any>> {
        let data;
        if (options['data']) {
            data = {
                payload: options['data']
            };
            delete options['data'];
        } else {
            data = {
                payload: Array.from({length: 100000}, () => Math.random()).join(' ')
            };
        }
        return this.http.post(url, data, {
            ...options, reportProgress: true, observe: 'events'
        });
    }

    /**
     * Reset the values of all fields.
     * @private
     */
    private resetState() {
        this.averageDownloadBandwidth = null;
        this.downloadProgress = 0;
        this.averageUploadBandwidth = null;
        this.uploadProgress = 0;
        this.latencySum = 0;
        this.packetsReceived = 0;
        this.averageLatency = 0;
        this.reachable = null;
        this.errorMessage = '';
        this.lastTimestamp = null;
        this.firstDownloadTimestamp = null;
        this.firstUploadTimestamp = null;
    }
}
